import { Button, cn } from '@arabat/ui';
import { useApi } from '@arabat/web-core';
import { useQuery } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSlug, useStorefront } from '../shared.js';

/*
 * "As soon as possible" or one of the branch's time slots (ADR-0123, amendment 1). The checkout
 * keeps one line ("Tomorrow 25 Oct · 5:30 pm — Change"); the time is picked in a sheet with three
 * wheels, day | hour | minute (owner, 2026-10-24). Each wheel lists only what is still free under
 * the wheels before it, so no combination can be refused.
 */

interface Slot {
  start: string;
  end: string;
  available: boolean;
}

interface Parts {
  day: string;
  hour: number;
  minute: number;
}

/** Day (YYYY-MM-DD), hour (0–23) and minute of an instant in the store's time zone. */
function partsOf(iso: string | number, timeZone: string): Parts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '0';
  return {
    day: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  };
}

/** The day after a YYYY-MM-DD key (calendar arithmetic, no time zone involved). */
function nextDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
}

/** Labels in the shopper's language, read in the store's time zone. */
function useLabels(timeZone: string, now: number) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-KW-u-nu-latn' : 'en-GB';
  const today = partsOf(now, timeZone).day;
  const tomorrow = nextDayKey(today);
  const noon = (key: string) => new Date(`${key}T12:00:00Z`);
  const date = (key: string) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(
      noon(key),
    );
  const day = (key: string) =>
    key === today
      ? t('checkout.today')
      : key === tomorrow
        ? t('checkout.tomorrow')
        : new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(noon(key));
  return {
    day,
    date,
    hour: (iso: string) =>
      new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true, timeZone }).format(
        new Date(iso),
      ),
    time: (iso: string) =>
      new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit', timeZone }).format(
        new Date(iso),
      ),
    when: (iso: string) => {
      const key = partsOf(iso, timeZone).day;
      return `${day(key)} ${date(key)} · ${new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone,
      }).format(new Date(iso))}`;
    },
  };
}

export function SlotPicker({
  branchId,
  fulfillment,
  value,
  onChange,
  asapAllowed,
}: {
  branchId: string | null;
  fulfillment: 'pickup' | 'delivery';
  value: string | null;
  onChange: (value: string | null) => void;
  asapAllowed: boolean;
}) {
  const api = useApi();
  const slug = useSlug();
  const { data: store } = useStorefront();
  const { t } = useTranslation();
  const slots = useQuery({
    queryKey: ['store', slug, 'slots', branchId, fulfillment],
    queryFn: () => api.store.slots(slug, { branch: branchId, fulfillment }),
    staleTime: 60_000,
  });
  // "Today" is fixed when the picker opens (a render must not read the clock).
  const [now] = useState(() => Date.now());
  const [sheet, setSheet] = useState(false);
  const timeZone = store?.timezone ?? 'Asia/Kuwait';
  const labels = useLabels(timeZone, now);
  const open = (slots.data?.slots ?? []).filter((s) => s.available);
  const card = (selected: boolean) =>
    cn(
      'flex flex-1 flex-col items-start gap-0.5 rounded-xl border-2 px-3 py-2.5 text-start transition-colors disabled:opacity-50',
      selected
        ? 'border-primary bg-primary/10'
        : 'border-border bg-surface hover:border-primary/50',
    );
  return (
    <div className="flex w-full min-w-0 flex-col gap-3">
      <div className="flex gap-2">
        {asapAllowed ? (
          <button
            type="button"
            aria-pressed={value === null}
            onClick={() => onChange(null)}
            className={card(value === null)}
          >
            <span className="font-semibold">{t('checkout.asap')}</span>
            <span className="text-xs text-fg-muted">{t('checkout.asapHint')}</span>
          </button>
        ) : null}
        <button
          type="button"
          aria-pressed={value !== null}
          disabled={open.length === 0}
          onClick={() => setSheet(true)}
          className={card(value !== null)}
        >
          <span className="font-semibold">{t('checkout.schedule')}</span>
          <span className="text-xs text-fg-muted">{t('checkout.scheduleHint')}</span>
        </button>
      </div>
      {value !== null ? (
        <button
          type="button"
          onClick={() => setSheet(true)}
          className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3 text-start hover:border-primary/50"
          data-testid="slot-summary"
        >
          <span aria-hidden className="text-lg">
            🕒
          </span>
          <span className="flex-1 font-semibold tabular-nums">{labels.when(value)}</span>
          <span className="text-sm font-semibold text-primary">{t('checkout.change')}</span>
        </button>
      ) : null}
      {slots.isSuccess && open.length === 0 ? (
        <p className="text-sm text-fg-muted">{t('checkout.noSlots')}</p>
      ) : null}
      {sheet && open.length > 0 ? (
        <SlotSheet
          slots={open}
          timeZone={timeZone}
          labels={labels}
          initial={value && open.some((s) => s.start === value) ? value : open[0]!.start}
          onClose={() => setSheet(false)}
          onConfirm={(start) => {
            onChange(start);
            setSheet(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** The sheet with the day, hour and minute wheels; nothing changes until "Confirm". */
function SlotSheet({
  slots,
  timeZone,
  labels,
  initial,
  onClose,
  onConfirm,
}: {
  slots: Slot[];
  timeZone: string;
  labels: ReturnType<typeof useLabels>;
  initial: string;
  onClose: () => void;
  onConfirm: (start: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.querySelector<HTMLElement>('[role="listbox"]')?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = before;
      window.removeEventListener('keydown', onKey);
    };
  }, []);
  const withParts = slots.map((s) => ({ ...s, ...partsOf(s.start, timeZone) }));
  const at = withParts.find((s) => s.start === draft) ?? withParts[0]!;
  const days = [...new Set(withParts.map((s) => s.day))];
  const ofDay = withParts.filter((s) => s.day === at.day);
  const hours = [...new Map(ofDay.map((s) => [s.hour, s])).values()];
  const ofHour = ofDay.filter((s) => s.hour === at.hour);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal
      aria-label={t('checkout.pickTime')}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('checkout.closeSheet')}
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panel}
        className="sheet-up relative flex w-full flex-col gap-4 rounded-t-2xl bg-surface p-4 shadow-xl sm:max-w-md sm:rounded-2xl"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto h-1.5 w-10 rounded-full bg-border sm:hidden" aria-hidden />
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('checkout.pickTime')}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('checkout.closeSheet')}
            className="grid size-9 place-items-center rounded-full bg-surface-muted text-xl"
          >
            <span aria-hidden>×</span>
          </button>
        </div>
        <div className="grid grid-cols-[1.6fr_1fr_1fr] gap-2">
          <Wheel
            label={t('checkout.day')}
            items={days.map((d) => ({ value: d, label: `${labels.day(d)} ${labels.date(d)}` }))}
            value={at.day}
            onChange={(day) => setDraft(withParts.find((s) => s.day === day)!.start)}
          />
          <Wheel
            label={t('checkout.hour')}
            items={hours.map((s) => ({ value: String(s.hour), label: labels.hour(s.start) }))}
            value={String(at.hour)}
            onChange={(hour) => setDraft(ofDay.find((s) => String(s.hour) === hour)!.start)}
          />
          <Wheel
            label={t('checkout.minute')}
            items={ofHour.map((s) => ({
              value: String(s.minute),
              label: String(s.minute).padStart(2, '0'),
            }))}
            value={String(at.minute)}
            onChange={(minute) => setDraft(ofHour.find((s) => String(s.minute) === minute)!.start)}
          />
        </div>
        <p className="text-center text-sm text-fg-muted" aria-live="polite">
          {t('checkout.slotChosen', { when: labels.when(at.start) })}
        </p>
        <Button type="button" size="lg" onClick={() => onConfirm(at.start)}>
          {t('checkout.confirmTime')}
        </Button>
      </div>
    </div>
  );
}

/** One row's height in the wheel (px); five rows show, the middle one is the choice. */
const ROW = 44;

/**
 * A scroll wheel: swipe (or mouse wheel) snaps a row into the middle band, a tap picks a row,
 * arrow keys step. A listbox to assistive technology.
 */
function Wheel({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const list = useRef<HTMLDivElement>(null);
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ items, value, onChange });
  useEffect(() => {
    latest.current = { items, value, onChange };
  });
  const index = Math.max(
    0,
    items.findIndex((i) => i.value === value),
  );
  // Keep the chosen row in the middle when the choice (or the rows) change from outside.
  useEffect(() => {
    const el = list.current;
    if (!el || Math.round(el.scrollTop / ROW) === index) return;
    if (typeof el.scrollTo === 'function') el.scrollTo({ top: index * ROW, behavior: 'smooth' });
    else el.scrollTop = index * ROW;
  }, [index, items.length]);
  useEffect(() => () => void (settle.current && clearTimeout(settle.current)), []);
  const onScroll = () => {
    if (settle.current) clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      const el = list.current;
      const { items: rows, value: current, onChange: change } = latest.current;
      if (!el || rows.length === 0) return;
      const row = rows[Math.min(rows.length - 1, Math.max(0, Math.round(el.scrollTop / ROW)))]!;
      if (row.value !== current) change(row.value);
    }, 120);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step =
      e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : e.key === 'Home' ? -index : 0;
    const to = e.key === 'End' ? items.length - 1 : index + step;
    if (to === index && e.key !== 'End') return;
    e.preventDefault();
    const row = items[Math.min(items.length - 1, Math.max(0, to))];
    if (row) onChange(row.value);
  };
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span id={`${id}-label`} className="text-center text-xs font-medium text-fg-muted">
        {label}
      </span>
      <div className="relative" style={{ height: ROW * 5 }}>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 rounded-lg bg-primary/10"
          style={{ top: ROW * 2, height: ROW }}
        />
        <div
          ref={list}
          role="listbox"
          tabIndex={0}
          aria-labelledby={`${id}-label`}
          aria-activedescendant={`${id}-${index}`}
          onScroll={onScroll}
          onKeyDown={onKeyDown}
          className="relative h-full snap-y snap-mandatory overflow-y-auto overscroll-contain rounded-lg [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none [&::-webkit-scrollbar]:hidden"
          style={{
            paddingBlock: ROW * 2,
            maskImage: 'linear-gradient(transparent, #000 35%, #000 65%, transparent)',
          }}
        >
          {items.map((item, i) => (
            <div
              key={item.value}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === index}
              onClick={() => onChange(item.value)}
              className={cn(
                'flex snap-center items-center justify-center truncate px-1 tabular-nums transition-colors select-none',
                i === index ? 'text-base font-bold text-fg' : 'text-sm text-fg-muted',
              )}
              style={{ height: ROW }}
            >
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
